import { invalidValueMsg, numberIsNotAllowedMsg, t } from '../i18n';
import { PHONE_NUMBER_REGEX, forceInternationalPhoneNumberFormat } from './phoneNumberUtils';

export const PASSWORD_REGEX = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}$/;
export const SSO_PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=(.*[\d\W+]){2})[A-Za-z\d\W+]{8,}$/;
export const POSTAL_CODE_PARTIAL_REGEX = /^[0-9]{0,4}[0|2-9]?$/;
export const POSTAL_CODE_REGEX = /^[0-9]{4}[0|2-9]$/;
export const POSTAL_CODE_WITH_PO_BOX_REGEX = /^[0-9]{5}$/;
export const SIM_CARD_REGEX = /^[0-9]{14}$/;
export const MAC_ADDRESS_REGEX =
  /^(?:[0-9A-Fa-f]{12}|(?:[0-9A-Fa-f]{2}:){5}[0-9A-Fa-f]{2}|(?:[0-9A-Fa-f]{2}-){5}[0-9A-Fa-f]{2})$/;
export const HOURS_AND_MINUTES_REGEX = /^([01]?\d|2[0-4])[:|.]([0-5]\d)$/;
export const ELISA_ICCID_PREFIX = '893580';
export const COST_CENTER_REGEX = /^\d{4}$/;
export const DEFAULT_COUNTRY_CODE = 'FIN';
export const DEFAULT_MAX_ALLOWED_ITEM_QUANTITY_FOR_ORDER = 99;
export const POSTAL_CODE_MAX_LENGTH = 5;
export const BILLING_ACCOUNT_MAX_LENGTH = 255;
export const BILLING_ACCOUNT_PAYER_NAME_EXTENSION_MAX_LENGTH = 100;
export const BILLING_ACCOUNT_NAME_EXT_MAX_LENGTH = 255;
export const BILLING_ACCOUNT_REFERENCE_TEXT_MAX_LENGTH = 40;
export const companyNameValidation = {
  maxLength: 256,
};
export const CUSTOMER_NUMBER_REGEX = /^([0-9]{3,9})$/;
export const DATE_FORMAT_REGEX = /^\d{1,2}\.\d{1,2}\.\d{4}$/;
export const ELECTRONIC_ADDRESS_REGEX = /(^0037|^73|^57|^TE0037|^FI)[A-Z0-9]+$/;
export const BUSINESS_ID_REGEX = /^[\d]{7}-[\d]$/;

export const createRegExpValidator = (re: RegExp, error: string) => (value: string) =>
  re.test(value) ? undefined : error;

export const ensureNonNullable = <T>(value: T, paramName: string): void => {
  if (value === null || value === undefined) {
    throw new Error(`Mandatory parameter "${paramName}" missing`);
  }
};

export const isPOBoxAddress = (postalCode?: string) => postalCode?.endsWith('1');

export const validatePhoneNumber = (
  value: string,
  subscriptionUserFriendlyId?: string,
  originalNumber?: string,
  numbers?: string[],
  index?: number,
  allOriginalChainTargetNumbers?: string[],
  allEditedChainTargetNumbers?: string[]
) => {
  if (!value) {
    return undefined;
  }

  // This is a special case
  // If the both value and original number is `0100` followed by the subscription user-friendly ID then this is allowed,
  const speciallyAllowedNumber = `010${subscriptionUserFriendlyId}`;
  const isOriginalNumberAllowed = originalNumber === speciallyAllowedNumber;
  const isValueSameAsOriginalNumber = value === originalNumber;

  // 0100 number can't be moved to another chain or another location within same chain
  const targetChainOrLocationChanged =
    value.startsWith('0100') &&
    allOriginalChainTargetNumbers?.indexOf(value) !== allEditedChainTargetNumbers?.indexOf(value);

  // 0100 number can only be used once within all chains
  const tooManySameTargetNumbers =
    value.startsWith('0100') && (allEditedChainTargetNumbers || []).filter(num => num === value).length > 1;

  if (tooManySameTargetNumbers || targetChainOrLocationChanged) {
    return t.PG0L(numberIsNotAllowedMsg);
  } else if (isOriginalNumberAllowed && isValueSameAsOriginalNumber) {
    return undefined;
  } else if (!PHONE_NUMBER_REGEX.test(value)) {
    return t.DUPA(invalidValueMsg);
  }

  const invalidPrefixes = ['116', '100', '200', '2020', '2022', '2023', '300', '600', '601', '602', '700', '900'];
  const invalidPrefixesWithCountryCode = invalidPrefixes
    .concat(invalidPrefixes.map(pre => `+358${pre}`))
    .concat(invalidPrefixes.map(pre => `358${pre}`))
    .concat(invalidPrefixes.map(pre => `0${pre}`));

  const number = forceInternationalPhoneNumberFormat(value.replace(/\s/g, ''))?.slice(4);
  return invalidPrefixesWithCountryCode.some(pre => number?.startsWith(pre))
    ? t.PG0L(numberIsNotAllowedMsg)
    : undefined;
};
